import React, { useCallback, useMemo, useState } from "react";

import { Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { MOBILE_MAX_SIZE } from "../../../constants";
import { useAppState } from "../../../context/StateContext";
import { Access, Options as AccessOption } from "../../../models/access";
import AccessService from "../../../services/access.service";
import useStyles, {
  GridWrapper,
  QuestionaryWrapper,
  SingleLineQuestionaryWrapper,
  SpacingInMobile,
} from "./styles";

const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const CACHE_TIME = 1 * 60 * 60 * 1000; // 1 hour

type Props = {
  access?: Access;
  onCustomerView?: boolean;
  hideTitle?: boolean;
  className?: string;
};

const AccessChecklistSummary = (props: Props) => {
  const { access, className, onCustomerView = false, hideTitle = false } = props;
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [drivewayLengthOptions, setDrivewayLengthOptions] = useState<AccessOption[]>([]);
  const [carryOptions, setCarryOptions] = useState<AccessOption[]>([]);

  useQuery(
    ["drivewayLength"],
    () =>
      AccessService.getAllDrivewayLengths({
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !onCustomerView,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setDrivewayLengthOptions(response.data);
        }
      },
    }
  );

  useQuery(
    ["carryDistance"],
    () =>
      AccessService.getAllCarryDistances({
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !onCustomerView,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setCarryOptions(response.data);
        }
      },
    }
  );

  const isMobile = window.innerWidth <= MOBILE_MAX_SIZE;

  const renderYesNo = useCallback((render?: boolean | null) => {
    if (render && render === true) return translate("yes");
    if (render === false) return translate("no");

    return "";
  }, []);

  const isValid = useCallback((value?: any) => {
    return value !== null && value !== undefined;
  }, []);

  const renderHasLiftValue = () => {
    if (isValid(access?.lift)) {
      if (access?.lift) return translate("yes");

      return translate("no");
    }

    return "";
  };

  const renderStepsValue = () => {
    if (access?.steps) {
      if (access.steps.toLowerCase() !== "no") {
        return `${renderYesNo(true)}${access?.stepType ? ` (${access?.stepType})` : ""}`;
      }

      return renderYesNo(false);
    }

    return "";
  };

  const drivewayLength = useMemo(() => {
    if (access?.driveway?.length) {
      const find = drivewayLengthOptions.find(
        (option) => option.value === access?.driveway?.length
      );

      if (find) return find.name;

      return "";
    }

    return "";
  }, [access, drivewayLengthOptions]);

  const carryDistance = useMemo(() => {
    if (access?.carryDistance) {
      const find = carryOptions.find((option) => option.value === access?.carryDistance);

      if (find) return find.name;

      return "";
    }

    return "";
  }, [access, carryOptions]);

  const treesPowerline = useMemo(() => {
    if (access?.treeOrPowerlineOverhanging && access.treeOrPowerlineLow) return "Overhanging / Low";
    if (access?.treeOrPowerlineOverhanging) return "Overhanging";
    if (access?.treeOrPowerlineLow) return "Low";

    return "";
  }, [access]);

  return (
    <div
      className={
        className
          ? classNames(classes.accessCheckListWrapper, className)
          : `px-2 py-3 ${classNames(classes.mainDiv, classes.accessCheckListWrapper)}`
      }
    >
      {!hideTitle && (
        <label className={classes.title}>{translate("accessChecklistSummary")}:</label>
      )}
      <Grid container direction="column">
        <GridWrapper item container xs={12}>
          <Grid item container xs={12} md={6} lg={6} alignItems="center" spacing={2}>
            <SpacingInMobile item xs={12} md={6} lg={6}>
              <QuestionaryWrapper>
                <span className="question">{translate("willTheEntryOfTheHouseBeObstructed")}</span>
                <span className="answer">
                  {isValid(access?.entryHouseObstructed)
                    ? renderYesNo(access?.entryHouseObstructed)
                    : ""}
                </span>
              </QuestionaryWrapper>
            </SpacingInMobile>
            <SpacingInMobile showInMobile={false} item xs={12} md={6} lg={6} />
          </Grid>
        </GridWrapper>
        <GridWrapper item container xs={12}>
          <Grid item xs={12} md={6} lg={6} container alignItems="center" spacing={2}>
            <SpacingInMobile item xs={12} md={6} lg={6} isMobile={isMobile}>
              <QuestionaryWrapper>
                <span className="question">{translate("driveway")}</span>
                <span className="answer">{access?.driveway?.name || ""}</span>
              </QuestionaryWrapper>
            </SpacingInMobile>
            <SpacingInMobile item xs={12} md={6} lg={6} hasPaddingTop={isMobile}>
              <QuestionaryWrapper>
                <span className="question">{translate("lengthM")}</span>
                <span className="answer">{drivewayLength}</span>
              </QuestionaryWrapper>
            </SpacingInMobile>
          </Grid>
        </GridWrapper>
        <GridWrapper item container xs={12}>
          <Grid item xs={12} md={6} lg={6} container alignItems="center" spacing={2}>
            <SpacingInMobile item xs={12} md={6} lg={6} isMobile={isMobile}>
              <QuestionaryWrapper>
                <span className="question">{translate("residenceType")}</span>
                <span className="answer">{access?.residence?.type || ""}</span>
              </QuestionaryWrapper>
            </SpacingInMobile>
            <SpacingInMobile item xs={12} md={6} lg={6} hasPaddingTop={isMobile}>
              <QuestionaryWrapper>
                <span className="question">{translate("level")}</span>
                <span className="answer">{access?.residence?.level || ""}</span>
              </QuestionaryWrapper>
            </SpacingInMobile>
          </Grid>
        </GridWrapper>
        <GridWrapper item container xs={12}>
          <Grid item xs={12} md={6} lg={6} container alignItems="center" spacing={2}>
            <SpacingInMobile item xs={12} md={6} lg={6} isMobile={isMobile}>
              <QuestionaryWrapper>
                <span className="question">{translate("steps")}</span>
                <span className="answer">{renderStepsValue()}</span>
              </QuestionaryWrapper>
            </SpacingInMobile>
            <SpacingInMobile item xs={12} md={6} lg={6} hasPaddingTop={isMobile}>
              <QuestionaryWrapper>
                <span className="question">{translate("hasLift")}</span>
                <span className="answer">{renderHasLiftValue()}</span>
              </QuestionaryWrapper>
            </SpacingInMobile>
          </Grid>
        </GridWrapper>
        <GridWrapper item container xs={12}>
          <Grid item container xs={12} alignItems="center" spacing={2}>
            <SpacingInMobile item xs={12}>
              <SingleLineQuestionaryWrapper>
                <span className="question">{translate("streetAccess")}</span>
                <span className="answer">
                  {access?.streetsAccesses && access?.streetsAccesses?.length
                    ? access?.streetsAccesses?.map((streetAccess) => streetAccess?.name).join(", ")
                    : ""}
                </span>
              </SingleLineQuestionaryWrapper>
            </SpacingInMobile>
          </Grid>
        </GridWrapper>
        <GridWrapper item container xs={12}>
          <Grid item xs={12} md={6} lg={6} container alignItems="center" spacing={2}>
            <SpacingInMobile item xs={12} md={6} lg={6} isMobile={isMobile}>
              <QuestionaryWrapper>
                <span className="question">{translate("parking")}</span>
                <span className="answer">{access?.parking || ""}</span>
              </QuestionaryWrapper>
            </SpacingInMobile>
            <SpacingInMobile item xs={12} md={6} lg={6} hasPaddingTop={isMobile}>
              <QuestionaryWrapper>
                <span className="question">{translate("carry")}</span>
                <span className="answer">{carryDistance}</span>
              </QuestionaryWrapper>
            </SpacingInMobile>
          </Grid>
        </GridWrapper>
        <GridWrapper item container xs={12}>
          <Grid item xs={12} md={6} lg={6} container alignItems="center" spacing={2}>
            <SpacingInMobile item xs={12} md={6} lg={6} isMobile={isMobile}>
              <QuestionaryWrapper>
                <span className="question">{translate("treesPowerline")}</span>
                <span className="answer">{treesPowerline}</span>
              </QuestionaryWrapper>
            </SpacingInMobile>
            <SpacingInMobile item xs={12} md={6} lg={6} hasPaddingTop={isMobile}>
              <QuestionaryWrapper>
                <span className="question">{translate("action")}</span>
                <span className="answer">{access?.treeOrPowerlineAction || ""}</span>
              </QuestionaryWrapper>
            </SpacingInMobile>
          </Grid>
        </GridWrapper>
        <GridWrapper item container xs={12}>
          <Grid item xs={12} md={6} lg={6} container alignItems="center" spacing={2}>
            <SpacingInMobile item xs={12} md={6} lg={6} isMobile={isMobile}>
              <QuestionaryWrapper>
                <span className="question">{translate("vehicleSize")}</span>
                <span className="answer">{access?.vehicle || ""}</span>
              </QuestionaryWrapper>
            </SpacingInMobile>
            <SpacingInMobile item xs={12} md={6} lg={6} hasPaddingTop={isMobile}>
              <QuestionaryWrapper>
                <span className="question">{translate("isFerryRequired")}</span>
                <span className="answer">
                  {isValid(access?.ferryRequired) ? renderYesNo(access?.ferryRequired) : ""}
                </span>
              </QuestionaryWrapper>
            </SpacingInMobile>
          </Grid>
        </GridWrapper>
      </Grid>

      {onCustomerView ? null : (
        <div className={classes.lastContent}>
          <span className="question">{translate("comments")}:</span>
          <span className="answer">
            <p>{access?.comments || ""}</p>
          </span>
        </div>
      )}
    </div>
  );
};

export default AccessChecklistSummary;
